// RMS triage outcome letters -> canonical analytics events.
//
// The RMS letter review is the same clinical question as the assessment report
// review, on a different surface: an AI drafts the paragraphs of a triage outcome
// letter, a clinician reads each one and approves, rewrites or resets it. So it
// reuses the same two rules rather than growing a second definition of them -
// textSimilarity for how much of the draft survived, reportDraftRecovery for
// which text the AI actually wrote. A second implementation of either would
// eventually disagree with the first, and then "AI adoption" would mean two
// different things depending on which product you asked about.
//
// Emitted from the browser, after each Firestore transaction commits, because the
// review page writes the letter directly - there is no callable to bridge from.
// No wording travels: counts, a percentage and codes only.

import { emitAnalyticsEvent } from './emitEvent';
import { MAX_STRING_LEN } from './eventRegistry';
import { charSimilarityPct, wordCount } from './textSimilarity';
import { draftForComparison } from './reportDraftRecovery';
import { noteReviewParagraphActivity } from './reviewSession';

/** Never let an analytics failure surface in a clinical review page. */
function safeEmit(fn) {
  try {
    return fn();
  } catch (error) {
    return false;
  }
}

function letterFields({ patientRecordId, letterType, pathway, code }) {
  if (!patientRecordId || !letterType || !code) return null;
  const fields = {
    patient_record_id: String(patientRecordId),
    letter_type: String(letterType).slice(0, MAX_STRING_LEN),
    paragraph_code: String(code).slice(0, MAX_STRING_LEN),
  };
  if (pathway) fields.pathway = String(pathway).slice(0, MAX_STRING_LEN);
  return fields;
}

/**
 * A clinician rewrote a drafted paragraph of a triage outcome letter.
 *
 * `priorHistory` is the paragraph's editHistory as it stood BEFORE this change,
 * which the review page already reads inside its own write transaction. It is
 * what lets the adoption numbers be computed here rather than by reading the
 * letter back out of Firestore: the text before a SECOND edit is the clinician's
 * own earlier wording, so without the history the comparison would measure the
 * last keystrokes instead of adoption of the draft.
 */
export function emitLetterParagraphEdited({
  patientRecordId, letterType, pathway, code, prevText, nextText, priorHistory,
}) {
  return safeEmit(() => {
    const fields = letterFields({ patientRecordId, letterType, pathway, code });
    if (!fields) return false;

    const payload = {
      ...fields,
      // Reset has an event of its own, so this one only ever describes a
      // rewrite - the same split as the assessment report events, so a count of
      // clinician rewrites means the same thing on both surfaces.
      edit_type: 'manual_edit',
    };
    if (typeof prevText === 'string') payload.prev_len = prevText.length;
    if (typeof nextText === 'string') payload.new_len = nextText.length;

    if (priorHistory !== undefined && typeof nextText === 'string') {
      const { draftText, regenerationCount } = draftForComparison(priorHistory, prevText);
      payload.word_count_ai = wordCount(draftText);
      payload.word_count_final = wordCount(nextText);
      payload.char_similarity_pct = charSimilarityPct(draftText, nextText);
      // The caveat rides with the numbers: a paragraph regenerated before any
      // clinician change is measured against its second draft, and one
      // regenerated after a change reads as edited when the text is pure AI.
      payload.regeneration_count = regenerationCount;
    }

    const queued = emitAnalyticsEvent('rms_letter_paragraph_edited', payload);
    // The active letter-review sitting counts what actually reached the
    // stream, so sitting counts and paragraph rows reconcile by construction.
    if (queued) {
      noteReviewParagraphActivity({
        action: 'edited',
        patientRecordId: fields.patient_record_id,
        letterType: fields.letter_type,
      });
    }
    return queued;
  });
}

/**
 * A clinician approved a drafted paragraph.
 *
 * @param {boolean} [opts.autoApproved] - False from the review page: a clinician
 *   pressed approve. The field exists because generation can mark paragraphs
 *   approved without anyone reading them, and an adoption rate that mixed the two
 *   would be meaningless.
 */
export function emitLetterParagraphApproved({
  patientRecordId, letterType, pathway, code, autoApproved = false,
}) {
  return safeEmit(() => {
    const fields = letterFields({ patientRecordId, letterType, pathway, code });
    if (!fields) return false;
    const queued = emitAnalyticsEvent('rms_letter_paragraph_approved', {
      ...fields,
      auto_approved: Boolean(autoApproved),
    });
    // Auto-approvals happen at generation with nobody reading, so only a
    // clinician's own approve counts towards the sitting.
    if (queued && !autoApproved) {
      noteReviewParagraphActivity({
        action: 'approved',
        patientRecordId: fields.patient_record_id,
        letterType: fields.letter_type,
      });
    }
    return queued;
  });
}

/** A clinician discarded their own rewrite and restored the drafted wording. */
export function emitLetterParagraphReset({
  patientRecordId, letterType, pathway, code,
}) {
  return safeEmit(() => {
    const fields = letterFields({ patientRecordId, letterType, pathway, code });
    if (!fields) return false;
    return emitAnalyticsEvent('rms_letter_paragraph_reset', fields);
  });
}
